import React, { forwardRef, useEffect, useState } from 'react';
import { useAppSelector } from '../app/hooks';
import { writeStateToUrl } from '../app/query-string';
import { useAppDispatch } from '../app/store';
import { resetFilters, updateFilter } from '../features/calendar/calendar-slice';
import { CombinedFilterData, FilterResponse, FilterText, FilterType } from '../helpers/common-types';
import { filterTypes } from '../helpers/filter-helpers';
import { MonthDropdown } from './month-dropdown';
import { SvgIcon } from './svg-icon';
import { ViewToggle } from './view-toggle';

export type FiltersProps = {
    showMonthDropdown: boolean;
    combinedFilterData: CombinedFilterData;
    includeWrapper?: boolean;
    filterCloseCallback?: () => void;
}

export const Filters = forwardRef<HTMLDivElement, FiltersProps>((props, ref) => {
    const { showMonthDropdown, combinedFilterData, includeWrapper = true, filterCloseCallback } = props;
    const dispatch = useAppDispatch();
    const { clearAllButtonText, venueFilterLabelText, categoryFilterLabelText, productionFilterLabelText,
        venueSelectAllDropdownOption, categorySelectAllDropdownOption, productionSelectAllDropdownOption,
        shareButtonText, applyFiltersButtonText, shareLinkCopiedTooltip, showShareButtons } = useAppSelector(state => state.settings);
    const currentAppliedFilters = useAppSelector(state => state.calendar.currentAppliedFilters);
    const currentDate = useAppSelector(state => state.calendar.currentDate);
    const layoutSetting = useAppSelector(state => state.calendar.layoutSetting);
    const [showShareTooltip, setShowShareTooltip] = useState(false);

    const filterLabels: FilterText = {
        [FilterType.LOCATION]: venueFilterLabelText,
        [FilterType.CATEGORIES]: categoryFilterLabelText,
        [FilterType.NAME]: productionFilterLabelText
    };

    const filterPlaceholders: FilterText = {
        [FilterType.LOCATION]: venueSelectAllDropdownOption,
        [FilterType.CATEGORIES]: categorySelectAllDropdownOption,
        [FilterType.NAME]: productionSelectAllDropdownOption
    }

    const shareClicked = () => {
        let shareUrl = '';
        const anyFiltersApplied = filterTypes.some(filterType => {
            return currentAppliedFilters[filterType].value.join() !== "";
        });
        if (anyFiltersApplied) {
            shareUrl = writeStateToUrl(undefined, currentAppliedFilters);
        }
        else {
            shareUrl = writeStateToUrl(currentDate);
        }
        navigator.clipboard.writeText(shareUrl);
        setShowShareTooltip(true);
        setTimeout(() => {
            setShowShareTooltip(false);
        }, 5000);
    }

    const FilterContents = () => {
        return (
            <div className="ace-cal-filters-inner">
                <div className="ace-cal-filters-filter-items">
                <ul className="ace-cal-filters-list">
                    {
                        filterTypes.map((filterType, index) => (
                            !!combinedFilterData[filterType] &&
                            <Filter key={index} filterType={filterType} filterOptions={combinedFilterData[filterType] || null} label={filterLabels[filterType]} placeholder={filterPlaceholders[filterType]} />
                        ))
                    }
                    {
                        showMonthDropdown &&
                        <li className="ace-cal-filter">
                            <MonthDropdown hideLabel={false} />
                        </li>
                    }
                </ul>
                <div className="flex-between ace-cal-clear-filters">
                        <button type="button" className="btn-link" onClick={() => dispatch(resetFilters())} data-testid="filters clearAll">{clearAllButtonText}</button>
                    {
                        showShareButtons &&
                        <button type="button" className={`btn-secondary ${showShareTooltip ? "show-tooltip" : ""}`} onClick={() => shareClicked()} data-tooltip={shareLinkCopiedTooltip} data-testid="filters share">
                            <span>{shareButtonText}</span>
                            <SvgIcon iconId="icon-link" />
                        </button>
                    }
                    {
                        filterCloseCallback &&
                        <button type="button" className="btn" onClick={() => filterCloseCallback()} data-testid="filters apply">
                            {applyFiltersButtonText}
                        </button>
                    }
                </div>
                </div>
                <ViewToggle />
            </div>
        )
    }

    return (
        <>
            {
                includeWrapper ?
                    <div className="ace-cal-filters-container" ref={ref}>
                        <div className="ace-cal-filters">
                            {FilterContents()}
                        </div>
                    </div>
                    :
                    FilterContents()
            }
        </>
    )

})

const Filter = (props: { filterType: FilterType, filterOptions: FilterResponse[] | null, label: string, placeholder: string }) => {
    const { filterType, filterOptions, label, placeholder } = props;
    const dispatch = useAppDispatch();
    const currentAppliedFilter = useAppSelector(state => state.calendar.currentAppliedFilters[filterType]);
    const [filterValue, setFilterValue] = useState(currentAppliedFilter?.value[0] || '');

    /*
     * Reflect external filter update, like through clearing or querystring
     */
    useEffect(() => {
        setFilterValue(currentAppliedFilter.value[0]);
    }, [currentAppliedFilter.value])

    /*
     * Trigger filter update via dropdown
     */
    useEffect(() => {
        if (filterOptions) {
            const matchingOption = filterOptions.filter(option => option.value == filterValue);
            dispatch(updateFilter({
                filterType: filterType,
                newState: {
                    value: [filterValue],
                    ...(!!matchingOption[0]?.firstOccurance && { firstOccurance: matchingOption[0]?.firstOccurance })
                }
            }));
        }
    }, [filterValue]);


    return (
        <>
            {!!filterOptions && filterOptions.length > 0 ?
                <li className="ace-cal-filter" key={filterType}>
                    <label htmlFor={`filter_${filterType}`}>{label}</label>
                    <select id={`filter_${filterType}`} onChange={(e) => setFilterValue(e.target.value)} name={`filterby_${filterType}`} value={filterValue}>
                        <option value="">
                            {placeholder}
                        </option>
                        {
                            filterOptions.map((filterOption, key) => (
                                <option value={filterOption.value} key={`${key}_${filterOption.value}`}>
                                    {filterOption.text}
                                </option>
                            ))
                        }
                    </select>
                </li>
                : null
            }
        </>
    )
}
